import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Globe, Truck, CreditCard, MapPin, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../services/api';
import { useMyAnagrafica } from '../../hooks/useMyAnagrafica';
import { useContatto } from '../../hooks/useContatto';
import { PAGAMENTI_IMMEDIATI, descriviPagamento, type MetodoPagamento } from '../../utils/pagamenti';

interface NotificheEmail {
  ordini: boolean;
  spedizioni: boolean;
  promozioni: boolean;
  newsletter: boolean;
}

interface CheckoutPref {
  destinazionePreferita: number | null;
  modalitaPagamentoPreferita: MetodoPagamento | null;
  modalitaSpedizionePreferita: string | null;
}

interface Preferenze {
  notificheEmail: NotificheEmail;
  lingua: string;
  checkout: CheckoutPref;
}

const SPEDIZIONI_OPTIONS = [
  { value: '', label: '— Usa default dal listino —' },
  { value: 'corriere', label: 'Corriere espresso' },
  { value: 'cash_carry', label: 'Ritiro Cash & Carry' },
];

export default function PreferenzePage() {
  const { data: ana } = useMyAnagrafica();
  // Un Contatto non ha ancora indirizzi su NTS: la sede è l'unica destinazione.
  const contatto = useContatto();
  const [pref, setPref] = useState<Preferenze | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api.get<Preferenze>('/api/clienti/me/preferenze')
      .then(r => setPref(r.data))
      .catch(e => setError(e?.response?.data?.detail ?? 'Errore caricamento preferenze'))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pref) return;
    setSubmitting(true);
    setError(null);
    setSuccess(false);
    try {
      const { data } = await api.patch<Preferenze>('/api/clienti/me/preferenze', pref);
      setPref(data);
      setSuccess(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail ?? 'Errore salvataggio preferenze');
    } finally {
      setSubmitting(false);
    }
  };

  const updateNotifica = (key: keyof NotificheEmail, value: boolean) => {
    setPref(p => p && { ...p, notificheEmail: { ...p.notificheEmail, [key]: value } });
  };

  const updateCheckout = (key: keyof CheckoutPref, value: any) => {
    setPref(p => p && { ...p, checkout: { ...p.checkout, [key]: value || null } });
  };

  const destPref = ana?.destinazionePreferita ?? 0;
  const destPrefDoc = (ana?.destinazioni ?? []).find(d => d.dd_coddest === destPref);
  const destinazionePredefinita = destPrefDoc ? (destPrefDoc.dd_nomdest || destPrefDoc.citta || `Destinazione ${destPref}`) : 'Sede legale';

  // I metodi ammessi per il conto: la condizione NTS del Cliente,
  // contrassegno e bonifico all'ordine (`orders/pagamenti.py`).
  const pagamentiAmmessi = ana?.pagamentiAmmessi?.length ? ana.pagamentiAmmessi : PAGAMENTI_IMMEDIATI;
  const pagamentiOptions = pagamentiAmmessi.map(m => ({
    value: m, label: descriviPagamento(m, { condizione: ana?.codicePagamentoLabel }).label,
  }));
  // Selezionato: la preferenza se ancora ammessa (la condizione su NTS può
  // cambiare), altrimenti il metodo della condizione in anagrafica.
  const pagamentoPreferito = pref?.checkout.modalitaPagamentoPreferita;
  const pagamentoSelezionato = pagamentoPreferito && pagamentiAmmessi.includes(pagamentoPreferito)
    ? pagamentoPreferito
    : ana?.pagamentoPredefinito && pagamentiAmmessi.includes(ana.pagamentoPredefinito)
      ? ana.pagamentoPredefinito : pagamentiAmmessi[0];

  return (
    <div data-testid="preferenze-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Preferenze</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Personalizza notifiche, lingua e i default che usiamo nel checkout.
        </p>
      </div>

      {loading ? (
        <div className="rounded-xl border bg-white p-12 flex items-center justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento preferenze…
        </div>
      ) : !pref ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error || 'Impossibile caricare le preferenze'}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5">
          {/* Notifiche */}
          <section className="rounded-xl border bg-white p-6 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Bell className="w-5 h-5 text-[hsl(210,64%,29%)]" />
              <h2 className="text-lg font-semibold">Notifiche email</h2>
            </div>
            <p className="text-sm text-muted-foreground">Decidi quando vuoi ricevere comunicazioni da DTK.</p>

            <ToggleRow
              label="Aggiornamenti sui miei ordini"
              hint="Conferma ordine, modifiche stato, ricezione DDT/fattura."
              checked={pref.notificheEmail.ordini}
              onChange={(v) => updateNotifica('ordini', v)}
              testId="notif-ordini"
            />
            <ToggleRow
              label="Tracking spedizioni"
              hint="Notifiche del corriere e DDT pronti al ritiro."
              checked={pref.notificheEmail.spedizioni}
              onChange={(v) => updateNotifica('spedizioni', v)}
              testId="notif-spedizioni"
            />
            <ToggleRow
              label="Promozioni dedicate"
              hint="Offerte a tempo e sconti riservati alla tua azienda."
              checked={pref.notificheEmail.promozioni}
              onChange={(v) => updateNotifica('promozioni', v)}
              testId="notif-promozioni"
            />
            <ToggleRow
              label="Newsletter settimanale"
              hint="Nuovi prodotti e novità dal magazzino DTK."
              checked={pref.notificheEmail.newsletter}
              onChange={(v) => updateNotifica('newsletter', v)}
              testId="notif-newsletter"
            />
          </section>

          {/* Lingua */}
          <section className="rounded-xl border bg-white p-6 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-5 h-5 text-[hsl(210,64%,29%)]" />
              <h2 className="text-lg font-semibold">Lingua del portale</h2>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="lingua-group">
              {[{ v: 'it', l: 'Italiano' }, { v: 'en', l: 'English' }].map(opt => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setPref(p => p && { ...p, lingua: opt.v })}
                  data-testid={`lingua-${opt.v}`}
                  className={`h-9 px-4 rounded-full border text-sm transition-colors ${
                    pref.lingua === opt.v
                      ? 'bg-[hsl(210,64%,29%)] text-white border-[hsl(210,64%,29%)]'
                      : 'bg-white text-[#3a3a36] hover:bg-[hsl(210,40%,97%)]'
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </section>

          {/* Checkout */}
          <section className="rounded-xl border bg-white p-6 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <CreditCard className="w-5 h-5 text-[hsl(210,64%,29%)]" />
              <h2 className="text-lg font-semibold">Default checkout</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              Imposta cosa preselezionare quando confermi un ordine. Puoi sempre cambiare al volo nel checkout.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* La destinazione predefinita è quella del Gestionale (`an_destin`),
                  la stessa per ordini web e telefonici: si imposta in Indirizzi
                  di spedizione. Qui c'era una preferenza solo del portale che il
                  checkout non leggeva (osservazioni 30/10, #8). */}
              <div data-testid="pref-destinazione">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                  <MapPin className="w-4 h-4" /> Destinazione predefinita
                </span>
                <p className="text-sm">
                  {destinazionePredefinita}{' '}
                  {!contatto && (
                    <Link to="/area-clienti/indirizzi-spedizione" className="text-[hsl(214,58%,41%)] hover:underline whitespace-nowrap">Cambia</Link>
                  )}
                </p>
              </div>
              <SelectField
                icon={<CreditCard className="w-4 h-4" />}
                label="Modalità pagamento"
                value={pagamentoSelezionato}
                onChange={(v) => updateCheckout('modalitaPagamentoPreferita', v)}
                testId="select-pagamento"
                options={pagamentiOptions}
              />
              <SelectField
                icon={<Truck className="w-4 h-4" />}
                label="Modalità spedizione"
                value={pref.checkout.modalitaSpedizionePreferita ?? ''}
                onChange={(v) => updateCheckout('modalitaSpedizionePreferita', v)}
                testId="select-spedizione"
                options={SPEDIZIONI_OPTIONS}
              />
            </div>
          </section>

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2" data-testid="pref-error">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center gap-2" data-testid="pref-success">
              <CheckCircle2 className="w-4 h-4" /> Preferenze salvate.
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              data-testid="pref-submit"
              className="h-10 px-6 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-semibold hover:bg-[hsl(210,64%,32%)] disabled:opacity-40 transition-colors"
            >
              {submitting ? 'Salvataggio…' : 'Salva preferenze'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testId }: {
  label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void; testId: string;
}) {
  return (
    <label className="flex items-start justify-between gap-4 py-2 border-t first:border-t-0">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5">{hint}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        data-testid={testId}
        className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${
          checked ? 'bg-[hsl(210,64%,29%)]' : 'bg-[hsl(0,0%,80%)]'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

function SelectField({ icon, label, value, onChange, options, testId }: {
  icon: React.ReactNode; label: string; value: string; onChange: (v: string) => void; testId: string;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-2.5 text-muted-foreground">{icon}</span>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full h-10 pl-9 pr-3 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30 appearance-none"
        >
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    </div>
  );
}
